// services/api.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
// Session management is handled internally without external UUID library

// Interfaz para citaciones de políticas
export interface Citation {
  policy_name: string;
  section: string;
  section_number?: string;
  section_name?: string;
  original_text?: string;
  relevance_score?: number;
  responsable?: string;
  archivo?: string;
}

// Interfaz para detalles de política (responsable, archivo)
export interface PolicyDetail {
  policy_id: string;
  policy_name: string;
  responsable?: string;
  archivo?: string;
  is_expired?: string;
}

// ADR-016: Interfaz para opciones de desambiguación
export interface DisambiguationOption {
  label: string;
  policy_key: string;
}

// Interfaz para mensajes del chat RAG de políticas
export interface ChatMessage {
  user_message: string;
  timestamp: number;
  // Campos para respuestas RAG de políticas
  answer?: string;
  citations?: Citation[];
  relevance_warning?: string;
  // Campos legacy para compatibilidad
  sql_query?: string;
  query_results?: {
    row_count: number;
    columns: string[];
    data: any[];
  } | null;
  interpretation?: string;
  needs_clarification?: boolean;
  clarification_suggestions?: string[];
  suggested_queries?: string[];
  // ADR-016: Opciones de desambiguación (cuando hay ambigüedad entre políticas)
  disambiguation_options?: DisambiguationOption[];
  policy_details?: PolicyDetail[];
  usage_hint?: string;
  query_analysis?: {
    query_type: string;
    complexity_level: string;
    entities: {
      brands: string[];
      models: string[];
      segments: string[];
      origins: string[];
      has_any_entity: boolean;
    };
    temporal_context: any;
  };
  context_applied?: {
    temporal?: string;
    auto_mapped_brands?: string;
    correction?: string;
  };
  success?: boolean;
  error_details?: string;
}

// Interfaz para request del chat RAG
export interface ChatRAGRequest {
  session_id: string;
  question: string;
  user_id?: string;
  usage_location?: string; // Código ISO de ubicación del usuario (ej: 'MX', 'BR', 'US')
  user_email?: string; // Email del usuario para detección de Elopak
}

// Interfaz para response del chat RAG
export interface ChatRAGResponse {
  answer: string;
  citations: Citation[];
  session_id: string;
  relevance_warning?: string;
  action?: string;
  card_type?: string;
  card?: any;
  suggested_queries?: string[];
  // ADR-016: Opciones de desambiguación para multi-turno
  disambiguation_options?: DisambiguationOption[];
  policy_details?: PolicyDetail[];
  usage_hint?: string;
}

export interface ChatHistory {
  messages: ChatMessage[];
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl = environment.apiUrl;

  // Subject para manejar el historial de chat
  private chatHistorySubject = new BehaviorSubject<ChatHistory>({
    messages: [],
  });
  public chatHistory$ = this.chatHistorySubject.asObservable();

  // Session ID para contexto conversacional RAG
  private sessionId: string;

  constructor(private http: HttpClient) {
    // Generar o recuperar session_id para contexto conversacional
    this.sessionId = this.getOrCreateSessionId();
  }

  // 🔐 Método para obtener headers con autenticación
  private getAuthHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    // Obtener token JWT de localStorage
    const jwtToken = localStorage.getItem('JWT');
    if (jwtToken) {
      headers = headers.set('Authorization', `Bearer ${jwtToken}`);
      console.log(
        '🔐 Token agregado a headers:',
        jwtToken.substring(0, 20) + '...',
      );
    } else {
      console.warn('⚠️ No se encontró token JWT en localStorage');
    }

    return headers;
  }

  // Obtener o crear session_id para contexto conversacional
  private getOrCreateSessionId(): string {
    const storedSessionId = localStorage.getItem('chat_session_id');
    const sessionExpiry = localStorage.getItem('chat_session_expiry');

    // Verificar si la sesión existe y no ha expirado (24 horas)
    if (storedSessionId && sessionExpiry) {
      const expiryTime = parseInt(sessionExpiry, 10);
      if (Date.now() < expiryTime) {
        return storedSessionId;
      }
    }

    // Crear nueva sesión
    const newSessionId = this.generateSessionId();
    const expiryTime = Date.now() + 12 * 60 * 60 * 1000; // 24 horas
    localStorage.setItem('chat_session_id', newSessionId);
    localStorage.setItem('chat_session_expiry', expiryTime.toString());

    return newSessionId;
  }

  // Generar un ID de sesión único
  private generateSessionId(): string {
    return (
      'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
    );
  }

  // Obtener session_id actual
  getSessionId(): string {
    return this.sessionId;
  }

  // Resetear sesión (para nueva conversación)
  resetSession(): void {
    this.sessionId = this.generateSessionId();
    const expiryTime = Date.now() + 12 * 60 * 60 * 1000;
    localStorage.setItem('chat_session_id', this.sessionId);
    localStorage.setItem('chat_session_expiry', expiryTime.toString());
  }

  // Obtener URL presignada para subir archivo
  getPresignedUrl(filename: string): Observable<any> {
    console.log('📡 Solicitando URL presignada para:', filename);
    return this.http.post(`${this.baseUrl}/upload-url`, { filename }).pipe(
      tap((response) => console.log('✅ Respuesta URL presignada:', response)),
      catchError((error) => {
        console.error('❌ Error obteniendo URL presignada:', error);
        throw error;
      }),
    );
  }

  // Subir archivo a S3
  uploadFile(presignedUrl: string, file: File): Observable<any> {
    console.log('📤 Subiendo archivo a S3:', presignedUrl);
    const headers = new HttpHeaders({
      'Content-Type':
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    return this.http
      .put(presignedUrl, file, {
        headers,
        observe: 'response',
      })
      .pipe(
        tap((response) =>
          console.log('✅ Archivo subido exitosamente:', response),
        ),
        catchError((error) => {
          console.error('❌ Error subiendo archivo:', error);
          throw error;
        }),
      );
  }

  // Verificar estado del procesamiento
  checkStatus(processId: string): Observable<any> {
    console.log('📊 Verificando estado del proceso:', processId);
    return this.http
      .post(`${this.baseUrl}/status`, { process_id: processId })
      .pipe(
        tap((response) => console.log('✅ Estado del proceso:', response)),
        catchError((error) => {
          console.error('❌ Error verificando estado:', error);
          throw error;
        }),
      );
  }

  // Enviar mensaje al chatbot legacy (SQL)
  sendChatMessage(message: string): Observable<any> {
    const currentHistory = this.chatHistorySubject.value;
    const historyForApi = currentHistory.messages.map((msg) => ({
      user_message: msg.user_message,
      sql_query: msg.sql_query,
      timestamp: msg.timestamp,
    }));

    const payload = {
      message,
      history: historyForApi,
    };

    console.log('💬 Enviando mensaje al chatbot:', payload);

    return this.http.post(`${this.baseUrl}/chat`, payload).pipe(
      tap((response) => console.log('✅ Respuesta del chatbot:', response)),
      catchError((error) => {
        console.error('❌ Error del chatbot:', error);
        throw error;
      }),
    );
  }

  // 🚀 NUEVO: Enviar pregunta al chat RAG de políticas
  sendPolicyQuestion(
    question: string,
    usageLocation?: string,
  ): Observable<ChatRAGResponse> {
    const payload: ChatRAGRequest = {
      session_id: this.sessionId,
      question: question,
    };

    // Agregar usage_location si está disponible (para detección automática de región)
    if (usageLocation) {
      payload.usage_location = usageLocation;
      console.log('📍 Incluyendo usage_location en el payload:', usageLocation);
    }

    // Agregar user_email para detección de Elopak
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      payload.user_email = userEmail;
      console.log('📧 Incluyendo user_email en el payload');
    }

    console.log('📋 Enviando pregunta de políticas:', payload);

    // 🔐 USAR HEADERS CON AUTENTICACIÓN
    const headers = this.getAuthHeaders();

    return this.http
      .post<ChatRAGResponse>(`${this.baseUrl}/api/v1/chat`, payload, {
        headers,
      })
      .pipe(
        tap((response) => {
          console.log('✅ Respuesta RAG de políticas:', response);
          // Actualizar session_id si el servidor devuelve uno diferente
          if (response.session_id && response.session_id !== this.sessionId) {
            this.sessionId = response.session_id;
            localStorage.setItem('chat_session_id', this.sessionId);
          }
        }),
        catchError((error) => {
          console.error('❌ Error en consulta de políticas:', error);
          throw error;
        }),
      );
  }

  // 🚀 NUEVO: Enviar selección de región
  sendRegionSelection(region: string): Observable<ChatRAGResponse> {
    // Estructura específica para Adaptive Card submit que espera el backend
    const payload = {
      session_id: this.sessionId,
      value: {
        action: 'select_region',
        region_selection: region,
      },
    };

    console.log('🌍 Enviando selección de región:', payload);

    const headers = this.getAuthHeaders();

    return this.http
      .post<ChatRAGResponse>(`${this.baseUrl}/api/v1/chat`, payload, {
        headers,
      })
      .pipe(
        tap((response) => {
          console.log('✅ Respuesta selección región:', response);
        }),
        catchError((error) => {
          console.error('❌ Error enviando selección de región:', error);
          throw error;
        }),
      );
  }

  // ADR-016: Enviar selección de política (desambiguación)
  sendPolicySelection(policyKey: string): Observable<ChatRAGResponse> {
    // Estructura específica para selección de política que espera el backend
    const payload = {
      session_id: this.sessionId,
      action: 'select_policy',
      policy_key: policyKey,
    };

    console.log('🔀 Enviando selección de política:', payload);

    const headers = this.getAuthHeaders();

    return this.http
      .post<ChatRAGResponse>(`${this.baseUrl}/api/v1/chat`, payload, {
        headers,
      })
      .pipe(
        tap((response) => {
          console.log('✅ Respuesta selección política:', response);
        }),
        catchError((error) => {
          console.error('❌ Error enviando selección de política:', error);
          throw error;
        }),
      );
  }

  // Agregar mensaje al historial
  addMessageToHistory(message: ChatMessage) {
    const currentHistory = this.chatHistorySubject.value;
    const updatedHistory = {
      messages: [...currentHistory.messages, message],
    };
    this.chatHistorySubject.next(updatedHistory);
  }

  // Limpiar historial y resetear sesión
  clearChatHistory() {
    this.chatHistorySubject.next({ messages: [] });
    this.resetSession(); // Nueva conversación = nueva sesión
  }

  // Obtener historial actual
  getCurrentHistory(): ChatHistory {
    return this.chatHistorySubject.value;
  }

  // Método público para actualizar historial (para uso desde componentes)
  updateChatHistory(history: ChatHistory) {
    this.chatHistorySubject.next(history);
  }

  // 🚀 NUEVO: Trigger manual de sincronización de políticas
  triggerSync(forceSync: boolean = false): Observable<any> {
    const payload = { force_sync: forceSync };
    const headers = this.getAuthHeaders();

    console.log('🔄 Triggering sync de políticas:', payload);

    return this.http
      .post(`${this.baseUrl}/api/v1/sync/trigger`, payload, { headers })
      .pipe(
        tap((response) => console.log('✅ Sync triggered:', response)),
        catchError((error) => {
          console.error('❌ Error triggering sync:', error);
          throw error;
        }),
      );
  }

  // 🚀 NUEVO: Obtener status de sincronización
  getSyncStatus(): Observable<any> {
    const headers = this.getAuthHeaders();

    console.log('📊 Obteniendo status de sync');

    return this.http
      .get(`${this.baseUrl}/api/v1/sync/status`, { headers })
      .pipe(
        tap((response) => console.log('✅ Sync status:', response)),
        catchError((error) => {
          console.error('❌ Error obteniendo sync status:', error);
          throw error;
        }),
      );
  }
}
